import {AdjacencyMap} from './GraphUtilConstants';

interface CyclicEdge {
  from: string;
  to: string;
}

// We need a unique string ID for each edge, to ensure that a Set() can be properly de-duped
const SEPARATOR = '#edge#';
const uniquelyIdentifyEdge: (from: string, to: string) => string = (from, to) => `${from}${SEPARATOR}${to}`;
const parseEdgeString: (s: string) => {from: string; to: string} = (s) => {
  const nodes = s.split(SEPARATOR);
  return {from: nodes[0], to: nodes[1]};
};

interface DetectCycleOptions {
  excludeLeadingEdges: boolean;
}

/**
 * Inspects the given graph for cycles. The input is a map of edges:
 *    {1: [2,3], 2: [3]}
 * represents the edges (1,2), (1,3), (2,3)
 *
 * If no cycles are detected, we return null. Otherwise, we return a list of
 * all node IDs that are in, or link to, a cycle. We also return a list of edges.
 * If excludeLeadingEdges is false, which it is by default, this includes both the edges that lead into the
 * cycle and the edges that form the cycle.
 * If excludeLeadingEdges is true, the list of edges only consists of those that form the cycle.
 *
 * Just to emphasize: we return not only the nodes in a cycle, but nodes that link to it as well.
 *
 * Intended to behave identically to its back-end counterpart, find_nodes_in_or_linking_to_cycle()
 *
 * TODO(vivian): Remove this function, and associated helper function, once loops are enabled
 */
export default function detectCycles(
  adjacencyMap: AdjacencyMap,
  options: DetectCycleOptions = {excludeLeadingEdges: false}
): {cyclicNodes: Array<string>; cyclicEdges: Array<CyclicEdge>} | null {
  const traversedNodes: Set<string> = new Set();
  const matchingNodes: Set<string> = new Set();
  const matchingEdges: Set<string> = new Set();
  const allNodeIds: Set<string> = new Set(Object.keys(adjacencyMap));

  for (const sourceNode of allNodeIds) {
    findNodesInOrLinkedToCycleFromSourceNode({
      adjacencyMap,
      traversedNodes,
      matchingNodes,
      matchingEdges,
      sourceNode,
      pathToNode: [],
      excludeLeadingEdges: options.excludeLeadingEdges,
    });
  }

  return matchingNodes.size > 0
    ? {
        cyclicNodes: [...matchingNodes],
        cyclicEdges: [...matchingEdges].map((edge) => parseEdgeString(edge)),
      }
    : null;
}

/**
 *
 * Helper function for detectCycles that finds nodes that are in or link to a cycle,
 *  starting at sourceNode
 *
 *  This function mutates
 *    - traversedNodes to add nodes after traversing them
 *    - matchingNodes to add nodes that are in or link to a cycle after finding them
 *    - matchingEdges to add all edges that form a cycle after finding them, plus the edges that lead into
 *      the cycle if excludeLeadingEdges is false
 *
 *  Note: traversedNodes is purely an optimization to skip re-traversing an already traversed node.
 *
 */
function findNodesInOrLinkedToCycleFromSourceNode({
  adjacencyMap,
  traversedNodes,
  matchingNodes,
  matchingEdges,
  sourceNode,
  pathToNode,
  excludeLeadingEdges,
}: {
  adjacencyMap: AdjacencyMap;
  traversedNodes: Set<string>;
  matchingNodes: Set<string>;
  matchingEdges: Set<string>;
  sourceNode: string;
  pathToNode: Array<string>;
  excludeLeadingEdges: Boolean;
}): void {
  if (traversedNodes.has(sourceNode)) {
    return;
  }

  const path = [...pathToNode, sourceNode];
  const adjacentNodes = adjacencyMap[sourceNode] ?? [];
  for (const adjacentNode of adjacentNodes) {
    const isAdjacentNodeInPath = path.includes(adjacentNode);
    const isAdjacentNodeInCycle = isAdjacentNodeInPath || matchingNodes.has(adjacentNode);
    const cycleStartIndex = isAdjacentNodeInPath ? path.findIndex((node) => node === adjacentNode) : 0;
    if (isAdjacentNodeInCycle) {
      path.forEach((matchingNode, index) => {
        matchingNodes.add(matchingNode);
        // excludeLeadingEdges is false by default (i.e. leading edges are included)
        if (index > 0 && (!excludeLeadingEdges || index > cycleStartIndex)) {
          matchingEdges.add(uniquelyIdentifyEdge(path[index - 1], matchingNode));
        }
        // Since this terminates the recursion we need to ensure that all paths that are part of
        // this cycle are tracked.
        for (const neighbor of adjacentNodes) {
          if (path.includes(neighbor)) {
            matchingEdges.add(uniquelyIdentifyEdge(sourceNode, neighbor));
          }
        }
      });
    } else {
      findNodesInOrLinkedToCycleFromSourceNode({
        adjacencyMap,
        traversedNodes,
        matchingNodes,
        matchingEdges,
        sourceNode: adjacentNode,
        pathToNode: path,
        excludeLeadingEdges,
      });
    }
  }
  traversedNodes.add(sourceNode);
}

/**
 * Inspects the given graph for cycles containing a specific node. The input is a map of edges:
 *    {1: [2,3], 2: [3]}
 * represents the edges (1,2), (1,3), (2,3)
 *
 * We return a set of cycles, where each cycle is a set of node (id)s traversed to reach the original node again.
 */
export function detectCyclesWithNode(adjacencyMap: AdjacencyMap, nodeId: string): Array<Array<string>> {
  const cycles: Set<Set<string>> = new Set();
  const nodesInPath: Set<string> = new Set();

  findNodesInCycleFromNode({
    adjacencyMap,
    currentNode: nodeId,
    sourceNode: nodeId,
    cycles,
    nodesInPath,
  });

  return [...cycles].map((cycle) => [...cycle]);
}

/**
 *
 * Helper function for detectCyclesWithNode that finds nodes that are in a cycle that loops back to sourceNode.
 *
 * This function mutates cycles to add a set of node ids that form a cycle when one is found.
 *
 * @param adjacencyMap a map of edges
 * @param nodeId the current node in the traversal
 * @param sourceNode the node which we are checking for paths that loop back to it
 * @param cycles the set to mutate when a cycle is found
 * @param nodesInPath the currently traversed path
 *
 */
function findNodesInCycleFromNode({
  adjacencyMap,
  currentNode,
  sourceNode,
  cycles,
  nodesInPath,
}: {
  adjacencyMap: AdjacencyMap;
  currentNode: string;
  sourceNode: string;
  cycles: Set<Set<string>>;
  nodesInPath: Set<string>;
}): void {
  const adjacentNodes = adjacencyMap[currentNode] ?? [];
  for (const adjacentNode of adjacentNodes) {
    if (adjacentNode === sourceNode) {
      cycles.add(new Set([...nodesInPath, sourceNode]));
      return;
    } else if (nodesInPath.has(adjacentNode)) {
      return;
    } else {
      findNodesInCycleFromNode({
        adjacencyMap,
        currentNode: adjacentNode,
        sourceNode,
        cycles,
        nodesInPath: new Set([...nodesInPath, adjacentNode]),
      });
    }
  }
}
